import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Text,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Skeleton,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { RxAvatar } from "react-icons/rx";

import {
  MdModeEditOutline,
  MdSearch,
  MdOutlineRemoveRedEye,
  MdMore,
} from "react-icons/md";
import { RiDeleteBinFill } from "react-icons/ri";
import CommonButton from "../../../components/commonbutton/Button";
import { reuseAbleColor } from "../../../components/Color";
import { useSnackbar } from "notistack";
import api from "../../../api";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { IoShareSocialOutline } from "react-icons/io5";
import { HiUserAdd } from "react-icons/hi";
import { MoonLoader } from "react-spinners";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const CustomSkeletonLoader = ({ count }) => {
  const skeletonRows = Array.from({ length: count }, (_, index) => (
    <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm animate-pulse">
      <div className="flex items-center space-x-3 mb-3">
        <Skeleton width="50px" height="50px" borderRadius="full" />
        <div className="flex-1">
          <Skeleton width="60%" height="20px" mb="2" />
          <Skeleton width="40%" height="16px" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Skeleton width="100%" height="16px" />
        <Skeleton width="100%" height="16px" />
        <Skeleton width="100%" height="16px" />
        <Skeleton width="100%" height="16px" />
      </div>
    </div>
  ));
  return skeletonRows;
};

const UserList = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedUser, setselectedUser] = useState(null);
  const [getUsers, setGetUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [gender, setGender] = useState("");
  const [department, setDepartment] = useState("");
  const [unit, setUnit] = useState("");
  const [level, setLevel] = useState("");
  const [staff_type, setStaffType] = useState("");
  const [pageLength, setPageLength] = useState(300);
  const [origin, setOrigin] = useState("");
  const [pf, setPf] = useState("");

  async function getUser(page, searchTerm = debouncedSearchTerm) {
    const response = await api.fetchUsers({
      params: {
        page,
        name: searchTerm,
        gender,
        staff_type,
        unit,
        department,
        level,
        state_of_origin: origin,
        pf_number: pf,
      },
    });
    return response;
  }

  async function getDepartment(page) {
    const response = await api.fethallDeparments();
    return response;
  }
  async function getUnit(page) {
    const response = await api.fetchDivision();
    return response;
  }

  async function getExportUser(page, searchTerm = debouncedSearchTerm) {
    const response = await api.fetchUsers({
      params: {
        name: searchTerm,
        page_length: pageLength,
        gender,
        staff_type,
        unit,
        department,
        level,
        state_of_origin: origin,
        pf_number: pf,
      },
    });
    return response;
  }

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { isLoading, isError, data, error, isPreviousData, refetch } = useQuery(
    [
      "users",
      page,
      debouncedSearchTerm,
      pageLength,
      gender,
      staff_type,
      unit,
      department,
      level,
      origin,
      pf,
    ],
    () => getUser(page, debouncedSearchTerm),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: "always",
    }
  );

  const exportDataQuery = useQuery(
    [
      "export",
      debouncedSearchTerm,
      pageLength,
      gender,
      staff_type,
      unit,
      department,
      level,
      origin,
    ],
    () => getExportUser(page, debouncedSearchTerm),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: "always",
    }
  );

  const departmentQuery = useQuery(["department"], () => getDepartment(), {
    keepPreviousData: true,
    refetchOnWindowFocus: "always",
  });
  const unitQuery = useQuery(["unit"], () => getUnit(), {
    keepPreviousData: true,
    refetchOnWindowFocus: "always",
  });

  const handleEdit = (id) => {
    navigate(`/settings/edit-staff-record/${id}`);
  };

  const handleView = (id) => {
    navigate(`/settings/staff-record/${id}`);
  };

  const handleDeleteClick = (user) => {
    setselectedUser(user);
    onOpen();
  };

  const handleDeleteConfirm = () => {
    if (selectedUser) {
      api
        .deleteUser(selectedUser.id)
        .then((response) => {
          const updatedgetUsers = getUsers.filter(
            (user) => user.id !== selectedUser.id
          );
          setGetUsers(updatedgetUsers);
          enqueueSnackbar("User deleted successfully", { variant: "success" });
          refetch();
        })
        .catch((error) => {
          enqueueSnackbar("Error deleting user", { variant: "error" });
        });
      onClose();
      setselectedUser(null);
    }
  };

  const handleDeleteCancel = () => {
    onClose();
    setselectedUser(null);
  };

  const roles = [
    { name: "Provost", value: "PT" },
    { name: "Deputy Provost", value: "DPT" },
    { name: "College Secretary", value: "CS" },
    { name: "Head of ASE", value: "HASE" },
    { name: "Head of NASE Junior", value: "HNASEJ" },
    { name: "Head of NASE Senior", value: "HNASES" },
    { name: "Head of Department", value: "HOD" },
    { name: "Head of Unit", value: "HOU" },
  ];
  const Levels = [
    "Level 1", "Level 2", "Level 3", "Level 4", "Level 5", "Level 6",
    "Level 7", "Level 8", "Level 9", "Level 10", "Level 11", "Level 12",
    "Level 13", "Level 14", "Level 15", "Level 16", "Level 17", "Level 18",
    "Level 19", "Level 20",
  ];

  const [selectedRole, setSelectedRole] = useState(null);
  const [isAssignModal, setIsAssignModal] = useState(false);
  const [userId, setUserId] = useState("");
  const [filter, setFilter] = useState(false);

  const handleCheckboxChange = (id) => {
    setSelectedRole(id);
  };

  async function handleSubmit(e) {
    e.preventDefault();

    if (!selectedRole) {
      enqueueSnackbar("Please select a role", { variant: "error" });
      return;
    }

    setLoading(true);

    try {
      const response = await api.updateUser({
        staffId: userId,
        user_supervision_role: selectedRole,
      });
      enqueueSnackbar("Role Assigned to Staff successfully", {
        variant: "success",
      });
      setLoading(false);
      setIsAssignModal(false);
      refetch();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
      setLoading(false);
    }
  }
  
  const handleAssignStaff = (id) => {
    setIsAssignModal(true);
    setUserId(id);
  };

  let exportData = exportDataQuery?.data?.data || [];

  const exportToExcel = () => {
    const data = exportData;
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(blob, "exportedData.xlsx");
  };

  return (
    <div className="min-h-screen bg-gray-50 p-2 md:p-4">
      {isLoading ? (
        <div className="bg-white shadow rounded-lg p-4">
          <p className="text-xl font-semibold mb-4 pb-4 border-b">
            Manage Users
          </p>
          <div className="space-y-4">
            <CustomSkeletonLoader count={6} />
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg">
          {/* Header */}
          <Box
            display="flex"
            alignItems="center"
            borderBottom="1px solid #EBEAED"
            px={{ base: "4", md: "6" }}
            py="4"
            justifyContent="space-between"
            flexDirection={{ base: "row", sm: "row" }}
            gap="4"
          >
            <Text className="text-lg md:text-xl font-semibold">Manage Users</Text>
            <Link to="/settings/add-user">
              <CommonButton title="Add Staff" />
            </Link>
          </Box>

          {/* Search and Controls */}
          <div className="p-4">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-4">
              <div className="flex items-center">
                <p className="text-base font-semibold text-gray-600 mb-0">
                  Total Users: {data?.meta?.total}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative">
                  <input
                    type="text"
                    className="border w-full sm:min-w-[220px] pl-8 pr-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-[#984779]"
                    placeholder="Search by staff name.."
                    onChange={(e) => setSearchTerm(e.target.value)}
                    value={searchTerm}
                  />
                  <MdSearch
                    size={18}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilter(!filter)}
                    className="px-4 py-2 bg-[#984779] text-white rounded-md text-sm font-medium hover:bg-[#7a3660] transition-colors"
                  >
                    Filter
                  </button>
                  <button
                    onClick={exportToExcel}
                    className="px-4 py-2 bg-[#984779] text-white rounded-md text-sm font-medium hover:bg-[#7a3660] transition-colors"
                  >
                    Download
                  </button>
                </div>
              </div>
            </div>

            {/* Filters */}
            {filter && (
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  <select
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#984779]"
                  >
                    <option value="">Select Department</option>
                    {departmentQuery.data?.data.map((division) => (
                      <option key={division.id} value={division.id}>
                        {division.name}
                      </option>
                    ))}
                  </select>
                  
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#984779]"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                  
                  <select
                    value={staff_type}
                    onChange={(e) => setStaffType(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#984779]"
                  >
                    <option value="">Select Staff Type</option>
                    <option value="NASE">NASE</option>
                    <option value="ASE">ASE</option>
                  </select>

                  <input
                    placeholder="State of origin"
                    value={origin}
                    onChange={(e) => setOrigin(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#984779]"
                  />

                  <input
                    placeholder="PF Number"
                    value={pf}
                    onChange={(e) => setPf(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#984779]"
                  />

                  <select
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#984779]"
                  >
                    <option value="">Select Level</option>
                    {Levels.map((level, index) => (
                      <option key={index} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>

                  <select
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#984779]"
                  >
                    <option value="">Select Unit</option>
                    {unitQuery.data?.data.map((division) => (
                      <option key={division.id} value={division.id}>
                        {division.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full table-auto border-collapse border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700">S/N</th>
                    <th className="border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700">Image</th>
                    <th className="border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700">Title</th>
                    <th className="border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700">Full Name</th>
                    <th className="border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700">Email</th>
                    <th className="border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700">PF</th>
                    <th className="border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700">Role</th>
                    <th className="border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                    <th className="border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700">Progress</th>
                    <th className="border border-gray-200 px-4 py-3 text-center text-sm font-medium text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {data?.data?.map((user, index) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="border border-gray-200 px-4 py-3 text-sm">{index + 1}</td>
                      <td className="border border-gray-200 px-4 py-3">
                        {user.image ? (
                          <Avatar h="40px" w="40px" src={user.image} />
                        ) : (
                          <RxAvatar size={40} color="#25324B" />
                        )}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-sm">{user.title}</td>
                      <td className="border border-gray-200 px-4 py-3 text-sm">{user.first_name} {user.last_name}</td>
                      <td className="border border-gray-200 px-4 py-3 text-sm">{user.email}</td>
                      <td className="border border-gray-200 px-4 py-3 text-sm">{user.staff_number}</td>
                      <td className="border border-gray-200 px-4 py-3 text-sm">{user.role}</td>
                      <td className="border border-gray-200 px-4 py-3 text-sm">{user.status}</td>
                      <td className="border border-gray-200 px-4 py-3 text-sm text-red-500">
                        {user?.profile_completion ? `${user?.profile_completion}%` : "Needs login"}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-center">
                        <Menu>
                          <MenuButton as={Button} bg="none" size="sm">
                            <MdMore size="20" color="#98A2B3" />
                          </MenuButton>
                          <MenuList>
                            <MenuItem onClick={() => handleView(user.id)}>
                              <MdOutlineRemoveRedEye size="16" className="mr-2" />
                              View
                            </MenuItem>
                            <MenuItem onClick={() => handleEdit(user.id)}>
                              <MdModeEditOutline size="16" className="mr-2" />
                              Edit
                            </MenuItem>
                            <MenuItem onClick={() => handleAssignStaff(user.id)}>
                              <HiUserAdd size="16" className="mr-2" />
                              Assign Role
                            </MenuItem>
                            <MenuItem onClick={() => handleDeleteClick(user)}>
                              <RiDeleteBinFill size="16" className="mr-2" color="#F44336" />
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
              {isLoading && !isPreviousData && (
                <div className="text-center py-4">Loading...</div>
              )}
              {data?.data?.map((user, index) => (
                <div key={user.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      {user.image ? (
                        <Avatar h="50px" w="50px" src={user.image} />
                      ) : (
                        <RxAvatar size={50} color="#25324B" />
                      )}
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">
                          {user.first_name} {user.last_name}
                        </h3>
                        <p className="text-xs text-gray-600">{user.title}</p>
                      </div>
                    </div>
                    <Menu>
                      <MenuButton as={Button} bg="none" size="sm">
                        <MdMore size="20" color="#98A2B3" />
                      </MenuButton>
                      <MenuList>
                        <MenuItem onClick={() => handleView(user.id)}>
                          <MdOutlineRemoveRedEye size="16" className="mr-2" />
                          View
                        </MenuItem>
                        <MenuItem onClick={() => handleEdit(user.id)}>
                          <MdModeEditOutline size="16" className="mr-2" />
                          Edit
                        </MenuItem>
                        <MenuItem onClick={() => handleAssignStaff(user.id)}>
                          <HiUserAdd size="16" className="mr-2" />
                          Assign Role
                        </MenuItem>
                        <MenuItem onClick={() => handleDeleteClick(user)}>
                          <RiDeleteBinFill size="16" className="mr-2" color="#F44336" />
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="font-medium text-gray-500">Email:</span>
                      <p className="text-gray-900 truncate">{user.email}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">PF:</span>
                      <p className="text-gray-900">{user.staff_number}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Role:</span>
                      <p className="text-gray-900">{user.role}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Status:</span>
                      <p className="text-gray-900">{user.status}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Department:</span>
                      <p className="text-gray-900 text-xs">
                        {user.department?.name || user.unit?.name || "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Progress:</span>
                      <p className="text-red-500">
                        {user?.profile_completion ? `${user?.profile_completion}%` : "Needs login"}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pagination */}
          {data && data.data && data?.data?.length > 0 && (
            <div className="px-4 py-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="text-sm text-gray-500">
                  Showing {data.meta.from} - {data.meta.to} of {data.meta.total} results
                  | Page {data.meta.current_page} of {data.meta.last_page}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={data?.meta?.current_page === 1 || isPreviousData}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setPage((prev) => prev + 1)}
                    disabled={data?.meta?.current_page === data?.meta?.last_page || isPreviousData}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modals */}
          <Modal isOpen={isOpen} onClose={handleDeleteCancel}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Confirm Delete</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                Are you sure you want to delete this user?
              </ModalBody>
              <ModalFooter>
                <Button colorScheme="red" mr={3} onClick={handleDeleteConfirm}>
                  Yes
                </Button>
                <Button onClick={handleDeleteCancel}>No</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          <Modal isOpen={isAssignModal} onClose={() => setIsAssignModal(false)}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader fontSize={{ base: "14px", md: "16px" }}>
                Assign A supervision Role To Staff
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                Select a role to be assigned to a staff
                {roles.map((option) => (
                  <div key={option.value} className="flex items-center gap-2 mt-4">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={selectedRole === option.value}
                      onChange={() => handleCheckboxChange(option.value)}
                    />
                    <label className="text-sm">{option.name}</label>
                  </div>
                ))}
              </ModalBody>
              <ModalFooter>
                <Button
                  colorScheme="red"
                  mr={3}
                  onClick={handleSubmit}
                  className="flex items-center gap-2"
                >
                  Confirm
                  {loading && <MoonLoader color="#fff" size={16} />}
                </Button>
                <Button onClick={() => setIsAssignModal(false)}>
                  Close
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default UserList;